import { LoanParams } from 'app/models/loan-params'

/**
 * Validation result for loan merge operations
 */
export interface MergeLoanValidationResult {
  canMerge: boolean
  errorMessage: string | null
}

/**
 * Validates whether selected loans can be merged and returns appropriate error message if not
 */
export function validateLoanMergeEligibility(
  selectedLoans: LoanParams[],
  indexRateOptions: any[],
): MergeLoanValidationResult {
  // Must have more than 1 loan selected
  if (selectedLoans.length <= 1) {
    return {
      canMerge: false,
      errorMessage: 'Please select at least 2 loans to merge.',
    }
  }

  // Check if all selected loans have index rate options with "Term" index type
  const hasValidIndexTypes = selectedLoans.every((loan: LoanParams) => {
    const indexRateOption = indexRateOptions.find(
      (option: any) => option.id === loan.indexOption?.id,
    )
    return indexRateOption && indexRateOption.indexType === 'TermIndex'
  })
  if (!hasValidIndexTypes) {
    return {
      canMerge: false,
      errorMessage:
        'All selected loans must have Term index type to be merged.',
    }
  }

  // Check if all maturity dates match
  const firstMaturityDate = selectedLoans[0]?.endDate
  const allMaturityDatesMatch = selectedLoans.every(
    (loan: LoanParams) => loan.endDate === firstMaturityDate,
  )
  if (!allMaturityDatesMatch) {
    return {
      canMerge: false,
      errorMessage:
        'All selected loans must have the same maturity date to be merged.',
    }
  }

  // Check if all selected loans have the same borrower
  const firstBorrowerId = selectedLoans[0]?.borrower?.id
  const allBorrowersMatch = selectedLoans.every(
    (loan: LoanParams) => loan.borrower?.id === firstBorrowerId,
  )
  if (!allBorrowersMatch) {
    return {
      canMerge: false,
      errorMessage:
        'All selected loans must have the same borrower to be merged.',
    }
  }

  // All validations passed
  return {
    canMerge: true,
    errorMessage: null,
  }
}

/**
 * Convenience function to check if loans can be merged (boolean only)
 */
export function canMergeLoans(
  selectedLoans: LoanParams[],
  indexRateOptions: any[],
): boolean {
  return validateLoanMergeEligibility(selectedLoans, indexRateOptions).canMerge
}

/**
 * Convenience function to get merge error message (null if no error)
 */
export function getMergeLoansErrorMessage(
  selectedLoans: LoanParams[],
  indexRateOptions: any[],
): string | null {
  return validateLoanMergeEligibility(selectedLoans, indexRateOptions)
    .errorMessage
}
